/** Clause-stage rich rendering — CUAD-style schema from the Kaycoo prompt. */
import { useState } from 'react';
import { ChevronRight, ChevronDown, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ConfidenceBadge } from './ConfidenceBadge';

const asArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const asText = (v: unknown): string => (typeof v === 'string' ? v : '');
const asBool = (v: unknown): boolean => Boolean(v);
const obj = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' ? (v as Record<string, unknown>) : {};

const RISK_BADGE: Record<string, string> = {
  LOW: 'bg-green-50 text-green-700 border-green-200',
  MEDIUM: 'bg-amber-50 text-amber-800 border-amber-200',
  HIGH: 'bg-orange-50 text-orange-800 border-orange-300',
  CRITICAL: 'bg-red-50 text-red-800 border-red-300',
};

function ClauseRow({ c }: { c: Record<string, unknown> }) {
  const [open, setOpen] = useState(false);
  const risk = asText(c.risk_level) || 'LOW';
  const riskClass = RISK_BADGE[risk] ?? 'bg-muted text-muted-foreground border-border';
  return (
    <Card className="border-border">
      <button
        type="button"
        onClick={() => { setOpen((o) => !o); }}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/50 rounded-t-lg"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
        )}
        <span className="text-xs font-mono text-muted-foreground shrink-0">
          {asText(c.clause_id) || '—'}
        </span>
        <span className="text-sm font-medium truncate flex-1">
          {asText(c.title) || asText(c.category) || '—'}
        </span>
        <span className="text-xs text-muted-foreground shrink-0">{asText(c.section_reference)}</span>
        <Badge variant="outline" className={`text-[10px] shrink-0 ${riskClass}`}>{risk}</Badge>
        <Badge variant="outline" className="text-[10px] shrink-0">{asText(c.category) || '—'}</Badge>
        {asText(c.cuad_type) && (
          <Badge
            variant="outline"
            className="text-[10px] shrink-0 bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/30 dark:text-indigo-300 dark:border-indigo-800"
            title="CUAD taxonomy type"
          >
            CUAD: {asText(c.cuad_type)}
          </Badge>
        )}
      </button>
      {open && (
        <CardContent className="pt-2 pb-3 px-3 space-y-2 text-xs border-t">
          {asText(c.verbatim_text) && (
            <blockquote className="italic bg-muted/50 rounded px-3 py-2 border border-muted whitespace-pre-wrap">
              {asText(c.verbatim_text)}
            </blockquote>
          )}
          {asText(c.risk_reason) && (
            <div>
              <span className="text-muted-foreground">Risk reason: </span>
              {asText(c.risk_reason)}
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
            <div>
              <span className="text-muted-foreground">Obligated: </span>
              {asArray(c.obligated_parties).join(', ') || '—'}
            </div>
            <div>
              <span className="text-muted-foreground">Beneficiary: </span>
              {asArray(c.beneficiary_parties).join(', ') || '—'}
            </div>
            <div>Relational language: {asBool(c.is_relational) ? 'yes' : 'no'}</div>
            <div>Standard wording: {asBool(c.is_standard_language) ? 'yes' : 'no'}</div>
            <div>One-sided: {asBool(c.is_one_sided) ? 'yes' : 'no'}</div>
            <div>Favours: {asText(c.favours_party) || '—'}</div>
            <div className="col-span-2">
              Negotiation recommended: {asBool(c.negotiation_recommended) ? 'YES' : 'no'}
            </div>
          </div>
          {asText(c.cuad_answer_span) && (
            <div className="rounded border border-indigo-200 bg-indigo-50/40 px-2 py-1 dark:border-indigo-800 dark:bg-indigo-950/20">
              <span className="text-[10px] uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
                CUAD answer span
              </span>
              <p className="font-mono text-[11px] mt-0.5">{asText(c.cuad_answer_span)}</p>
            </div>
          )}
          {asText(c.clause_notes) && (
            <p className="italic text-muted-foreground border-l-2 pl-2">
              {asText(c.clause_notes)}
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export function RichClauseBody({ output }: { output: Record<string, unknown> }) {
  const clauses = asArray(output.clauses);
  const missing = asArray(output.missing_standard_clauses);
  const stats = obj(output.clause_statistics);
  const byRisk = obj(stats.by_risk_level);
  const highestRisk = asArray(output.highest_risk_clauses).map((x) => (typeof x === 'string' ? x : ''));
  const balance = asText(output.overall_contract_balance);
  const flags = asArray(output.flags_for_downstream).map((x) => (typeof x === 'string' ? x : ''));
  const notes = asText(output.extraction_notes);
  const score = output.clause_confidence_score;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {balance && <Badge variant="outline" className="text-xs">{balance}</Badge>}
        {Object.entries(byRisk).map(([k, v]) => (
          <Badge
            key={k}
            variant="outline"
            className={`text-[10px] ${RISK_BADGE[k] ?? ''}`}
          >
            {k}: {typeof v === 'number' ? v : 0}
          </Badge>
        ))}
        <span className="ml-auto">
          <ConfidenceBadge score={score} rationale={notes} />
        </span>
      </div>

      {highestRisk.length > 0 && (
        <div className="rounded border border-red-300 bg-red-50 p-3 text-sm dark:border-red-700 dark:bg-red-950/30">
          <p className="font-medium text-red-800 dark:text-red-300 flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
            Highest-risk clauses ({highestRisk.length})
          </p>
          <p className="text-xs text-red-700 dark:text-red-200 mt-0.5">
            {highestRisk.join(', ')}
          </p>
        </div>
      )}

      {missing.length > 0 && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-950/30">
          <p className="font-medium text-amber-800 dark:text-amber-300 mb-1">
            Missing standard clauses ({missing.length})
          </p>
          <ul className="text-xs text-amber-700 dark:text-amber-200 space-y-1">
            {missing.map((m, i) => {
              const o = obj(m);
              const riskCls = RISK_BADGE[asText(o.risk_if_absent)] ?? '';
              return (
                <li key={i} className="flex items-start gap-2">
                  <Badge variant="outline" className={`text-[10px] shrink-0 ${riskCls}`}>
                    {asText(o.risk_if_absent) || '—'}
                  </Badge>
                  <div>
                    <span className="font-medium">{asText(o.clause_type) || '—'}</span>
                    {asText(o.reason_expected) && (
                      <span className="text-muted-foreground"> — {asText(o.reason_expected)}</span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="space-y-1.5">
        {clauses.map((c, i) => (
          <ClauseRow key={i} c={obj(c)} />
        ))}
      </div>

      {flags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {flags.map((f, i) => (
            <span
              key={i}
              className="text-[10px] rounded bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 px-1.5 py-0.5"
            >
              {f}
            </span>
          ))}
        </div>
      )}

      {notes && (
        <p className="text-xs italic text-muted-foreground border-l-2 pl-2">
          Extraction notes: {notes}
        </p>
      )}
    </div>
  );
}
